import { action, computed, flow, makeObservable, observable, toJS } from 'mobx';
import { ApiResponse, GlobalStore } from '@roc/feature-app-core';
import { AIDocType } from '@roc/feature-documents';
import { TaskStatus } from '@roc/feature-general-tasks';
import { DocReviewInternalService } from '../../service/docReviewInternalService';
import { DocReviewBaseService } from '../../service/docReviewBaseService';
import { ReviewLogStore } from '../../store/reviewLogStore';
import { getTypeKey } from '../../utils/utils';

export type QcFilter = 'ALL' | 'NEEDS_REVISION';

export interface QcFile {
  documentId: number;
  fileName: string;
  uploadedDate?: string;
}

export interface QcField {
  itemName: string;
  itemDisplayName: string;
  expectedData: string | number | null;
  data: string | number | null;
  needsRevisionFlag: boolean;
  sendForRevision: boolean;
  reviewerComment: string | null;
}

export interface QcSection {
  type: string;
  sectionName: string;
  status?: string;
  errorMessage?: string;
  documents: QcFile[];
  items: QcField[];
}

export interface QcFieldRef {
  sectionName: string;
  itemName: string;
}

export interface QcCounts {
  total: number;
  flagged: number;
}

export interface QcSectionGroup {
  group: string;
  sections: QcSection[];
}

export type QcFileState =
  | { status: 'loading' }
  | { status: 'loaded'; data: ArrayBuffer; headers: Record<string, string> }
  | { status: 'error' };

const GROUP_ORDER = ['Title', 'Entity', 'Insurance'];
const OTHER_GROUP = 'Other';
const postQcDocType = (): AIDocType => AIDocType.POST_CLOSING_REVIEW;

export const isBlank = (value: unknown): boolean =>
  value === null || value === undefined || String(value).trim() === '';

export const fieldKey = (ref: QcFieldRef): string => `${ref.sectionName}::${ref.itemName}`;

const fileKey = (section: QcSection, documentId: number): string => `${section.type}:${documentId}`;

const sameField = (a: QcFieldRef, b: QcFieldRef): boolean =>
  !!a && !!b && a.sectionName === b.sectionName && a.itemName === b.itemName;

const allFields = (sections: QcSection[]): QcField[] =>
  sections.reduce((fields, section) => fields.concat(section.items), [] as QcField[]);

const countFields = (fields: QcField[]): QcCounts =>
  fields.reduce(
    (counts, field) => ({
      total: counts.total + 1,
      flagged: counts.flagged + (field.sendForRevision ? 1 : 0),
    }),
    { total: 0, flagged: 0}
  );

const toField = (item: any): QcField => ({
  itemName: item.itemName,
  itemDisplayName: item.itemDisplayName ?? item.itemName,
  expectedData: item.expectedData ?? null,
  data: item.data ?? null,
  needsRevisionFlag: !!item.needsRevisionFlag,
  sendForRevision: !!item.sendForRevision,
  reviewerComment: item.reviewerComment ?? null,
});

const toSection = (section: any): QcSection => ({
  type: section.type ?? OTHER_GROUP,
  sectionName: section.sectionName,
  status: section.status ?? undefined,
  errorMessage: section.errorMessage ?? undefined,
  documents: Array.isArray(section.documents) ? section.documents : [],
  items: (Array.isArray(section.items) ? section.items : [])
    .filter(item => item && item.itemName)
    .map(toField),
});

const parseRawSections = (raw: unknown): any[] => {
  const parsed = typeof raw === 'string' ? JSON.parse(raw.trim() === '' ? '[]' : raw) : raw;
  return Array.isArray(parsed) ? parsed.filter(section => section && section.sectionName) : [];
};

export class PostQcReviewStore {
  globalStore: GlobalStore;
  reviewLogStore: ReviewLogStore;
  private service = new DocReviewInternalService();
  loanId: number = null;
  sections: QcSection[] = [];
  filter: QcFilter = 'ALL';
  expandedSection: string = null;
  focusedField: QcFieldRef = null;
  selectedFileBySection = new Map<string, number>();
  files = new Map<string, QcFileState>();
  dirtySections = new Set<string>();
  editingNotes = new Set<string>();
  noteOriginals = new Map<string, string>();
  private editVersions = new Map<string, number>();
  loading = false;
  saving = false;
  loadError = false;
  lastSavedAt: Date = null;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.reviewLogStore = new ReviewLogStore(globalStore);
    makeObservable(this, {
      loanId: observable,
      sections: observable,
      filter: observable,
      expandedSection: observable,
      focusedField: observable.ref,
      selectedFileBySection: observable,
      files: observable.shallow,
      dirtySections: observable,
      editingNotes: observable,
      noteOriginals: observable,
      loading: observable,
      saving: observable,
      loadError: observable,
      lastSavedAt: observable,
      counts: computed,
      groups: computed,
      visibleGroups: computed,
      expandedSectionModel: computed,
      navigableFields: computed,
      canSendRevision: computed,
      isDirty: computed,
      load: flow,
      save: flow,
      saveNote: flow,
      sendRevision: flow,
      loadFile: flow,
      fillCannedComment: flow,
      setFilter: action,
      selectSection: action,
      toggleFlag: action,
      focusField: action,
      moveFocus: action,
      selectFile: action,
      setFlag: action,
      setComment: action,
      editNote: action,
      cancelNoteEdit: action,
      reset: action,
    });
  }

  private get typeKey(): string {
    return getTypeKey(postQcDocType());
  }

  get counts(): QcCounts {
    return countFields(allFields(this.sections));
  }

  get groups(): QcSectionGroup[] {
    const groupOf = (section: QcSection) =>
      GROUP_ORDER.includes(section.type) ? section.type : OTHER_GROUP;
    return [...GROUP_ORDER, OTHER_GROUP]
      .map(group => ({ group, sections: this.sections.filter(section => groupOf(section) === group) }))
      .filter(entry => entry.sections.length > 0);
  }

  get visibleGroups(): QcSectionGroup[] {
    if (this.filter === 'ALL') {
      return this.groups;
    }
    if (!this.sections.some(section => this.matchesFilter(section))) {
      return [];
    }
    return this.groups
      .map(entry => ({
        group: entry.group,
        sections: entry.sections.filter(section =>
          this.matchesFilter(section) || section.sectionName === this.expandedSection
        ),
      }))
      .filter(entry => entry.sections.length > 0);
  }

  get expandedSectionModel(): QcSection {
    return this.sections.find(section => section.sectionName === this.expandedSection) ?? null;
  }

  get navigableFields(): QcFieldRef[] {
    return this.orderedRefs(section => this.visibleItems(section));
  }

  get canSendRevision(): boolean {
    return this.counts.flagged > 0;
  }

  get isDirty(): boolean {
    return this.dirtySections.size > 0;
  }

  sectionCounts(section: QcSection): QcCounts {
    return countFields(section.items);
  }

  visibleItems(section: QcSection): QcField[] {
    if (this.filter === 'NEEDS_REVISION') {
      return section.items.filter(item => item.sendForRevision);
    }
    return section.items;
  }

  selectedFileId(section: QcSection): number {
    return this.selectedFileBySection.get(section.sectionName) ?? section.documents[0]?.documentId ?? null;
  }

  fileState(section: QcSection, documentId: number): QcFileState {
    return this.files.get(fileKey(section, documentId));
  }

  isFocused(ref: QcFieldRef): boolean {
    return sameField(this.focusedField, ref);
  }

  isEditingNote(ref: QcFieldRef): boolean {
    return this.editingNotes.has(fieldKey(ref));
  }

  hasPostedNote(ref: QcFieldRef): boolean {
    const key = fieldKey(ref);
    return this.noteOriginals.has(key) && !isBlank(this.noteOriginals.get(key));
  }

  private findSection(sectionName: string): QcSection {
    return this.sections.find(section => section.sectionName === sectionName) ?? null;
  }

  private findField(ref: QcFieldRef): QcField {
    return this.findSection(ref.sectionName)?.items.find(item => item.itemName === ref.itemName) ?? null;
  }

  private orderedRefs(pick: (section: QcSection) => QcField[]): QcFieldRef[] {
    const refs: QcFieldRef[] = [];
    this.groups.forEach(group =>
      group.sections.forEach(section =>
        pick(section).forEach(item => refs.push({ sectionName: section.sectionName, itemName: item.itemName }))
      )
    );
    return refs;
  }

  private markDirty(sectionName: string) {
    this.dirtySections.add(sectionName);
    this.editVersions.set(sectionName, (this.editVersions.get(sectionName) ?? 0) + 1);
  }

  private dropHiddenFocus() {
    const focused = this.focusedField;
    const visible = !!focused
      && this.expandedSection === focused.sectionName
      && this.navigableFields.some(ref => sameField(ref, focused));
    if (focused && !visible) {
      this.focusedField = null;
    }
  }

  private matchesFilter(section: QcSection): boolean {
    return this.filter === 'ALL' || this.visibleItems(section).length > 0;
  }

  private firstMatchingSection(): string {
    for (const entry of this.groups) {
      const match = entry.sections.find(section => this.matchesFilter(section));
      if (match) {
        return match.sectionName;
      }
    }
    return null;
  }

  setFilter(filter: QcFilter) {
    this.filter = filter;
    const current = this.expandedSectionModel;
    if (!current || !this.matchesFilter(current)) {
      const first = this.firstMatchingSection();
      if (first) {
        this.expandedSection = first;
      }
    }
    this.dropHiddenFocus();
  }

  selectSection(sectionName: string) {
    this.expandedSection = sectionName;
    this.dropHiddenFocus();
  }

  toggleFlag(ref: QcFieldRef) {
    const field = this.findField(ref);
    if (!field) {
      return;
    }
    this.setFlag(ref, !field.sendForRevision);
  }

  focusField(ref: QcFieldRef) {
    this.focusedField = ref;
    if (ref) {
      this.expandedSection = ref.sectionName;
    }
  }

  moveFocus(step: 1 | -1) {
    const fields = this.navigableFields;
    if (fields.length === 0) {
      return;
    }
    const index = fields.findIndex(ref => sameField(ref, this.focusedField));
    const next = index === -1
      ? (step === 1 ? 0 : fields.length - 1)
      : Math.min(Math.max(index + step, 0), fields.length - 1);
    this.focusField(fields[next]);
  }

  selectFile(section: QcSection, documentId: number) {
    this.selectedFileBySection.set(section.sectionName, documentId);
  }

  setFlag(ref: QcFieldRef, flagged: boolean) {
    const field = this.findField(ref);
    if (!field || field.sendForRevision === flagged) {
      return;
    }
    const key = fieldKey(ref);
    field.sendForRevision = flagged;
    this.markDirty(ref.sectionName);
    this.reviewLogStore.addEvent({
      docType: postQcDocType(),
      mapping: 'sendForRevision',
      sectionName: ref.sectionName,
      itemDisplayName: field.itemDisplayName,
      value: flagged,
    });
    if (flagged) {
      this.editingNotes.add(key);
      if (isBlank(field.reviewerComment)) {
        this.fillCannedComment(ref);
      }
      return;
    }
    field.reviewerComment = null;
    this.editingNotes.delete(key);
    this.noteOriginals.delete(key);
  }

  setComment(ref: QcFieldRef, comment: string) {
    const field = this.findField(ref);
    if (!field) {
      return;
    }
    field.reviewerComment = comment;
    this.markDirty(ref.sectionName);
  }

  editNote(ref: QcFieldRef) {
    const field = this.findField(ref);
    if (!field) {
      return;
    }
    this.noteOriginals.set(fieldKey(ref), field.reviewerComment ?? '');
    this.editingNotes.add(fieldKey(ref));
  }

  cancelNoteEdit(ref: QcFieldRef) {
    const key = fieldKey(ref);
    const field = this.findField(ref);
    if (field && this.noteOriginals.has(key)) {
      field.reviewerComment = this.noteOriginals.get(key);
    }
    this.noteOriginals.delete(key);
    this.editingNotes.delete(key);
  }

  *saveNote(ref: QcFieldRef) {
    if (!this.findField(ref)) {
      return false;
    }
    const saved: boolean = yield this.save([ref.sectionName]);
    if (saved) {
      this.noteOriginals.delete(fieldKey(ref));
      this.editingNotes.delete(fieldKey(ref));
      this.globalStore.notificationStore.showSuccessNotification({ message: 'Note saved' });
    }
    return saved;
  }

  *fillCannedComment(ref: QcFieldRef) {
    const section = this.findSection(ref.sectionName);
    if (!section) {
      return;
    }
    try {
      const response: ApiResponse = yield this.service.getRevisionComment(this.typeKey, `${section.type}-${ref.itemName}`);
      const comment = response?.data?.data?.comment;
      const field = this.findField(ref);
      if (field && field.sendForRevision && isBlank(field.reviewerComment) && !isBlank(comment)) {
        field.reviewerComment = comment;
        this.markDirty(ref.sectionName);
      }
    } catch (e) {
      return;
    }
  }

  *load(loanId: number, taskStatus?: string) {
    this.reset();
    this.loanId = loanId;
    this.loading = true;
    try {
      const response: ApiResponse = yield (this.service as DocReviewBaseService).getDocReviewData(loanId, this.typeKey, true);
      const rawSections = parseRawSections(response?.data?.data);
      this.sections = rawSections.map(toSection);
      this.sections.forEach(section => section.items
        .filter(item => item.sendForRevision && isBlank(item.reviewerComment))
        .forEach(item => this.editingNotes.add(fieldKey({ sectionName: section.sectionName, itemName: item.itemName }))));
      this.filter = taskStatus === TaskStatus.MORE_INFO_NEEDED ? 'NEEDS_REVISION' : 'ALL';
      this.expandedSection = this.firstMatchingSection() ?? this.groups[0]?.sections[0]?.sectionName ?? null;
    } catch (e) {
      this.loadError = true;
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error getting review data',
      });
    } finally {
      this.loading = false;
    }
  }

  *save(sectionNames?: string[]) {
    const pending = Array.from(this.dirtySections).filter(sectionName => !sectionNames || sectionNames.includes(sectionName));
    if (pending.length === 0) {
      return true;
    }
    this.saving = true;
    try {
      const sent = new Map<string, number>();
      pending.forEach(sectionName => sent.set(sectionName, this.editVersions.get(sectionName) ?? 0));
      yield this.reviewLogStore.logEvents(this.loanId, postQcDocType());
      const payload = this.sections
        .filter(section => sent.has(section.sectionName))
        .map(section => toJS(section));
      yield this.service.saveDocReviewData(this.loanId, this.typeKey, payload);
      sent.forEach((version, sectionName) => {
        if ((this.editVersions.get(sectionName) ?? 0) === version) {
          this.dirtySections.delete(sectionName);
        }
      });
      this.lastSavedAt = new Date();
      return true;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error saving review data',
      });
      return false;
    } finally {
      this.saving = false;
    }
  }

  *sendRevision() {
    const saved: boolean = yield this.save();
    if (!saved) {
      return false;
    }
    this.saving = true;
    try {
      yield this.service.sendRevisionDoc(this.loanId, this.typeKey);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Revision document sent',
      });
      return true;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Review saved, but the revision was not sent',
      });
      return false;
    } finally {
      this.saving = false;
    }
  }

  *loadFile(section: QcSection, documentId: number) {
    const key = fileKey(section, documentId);
    const current = this.files.get(key);
    if (current && current.status !== 'error') {
      return;
    }
    this.files.set(key, { status: 'loading' });
    try {
      const response = yield this.service.getPostQcDocument(this.loanId, section.sectionName, documentId);
      this.files.set(key, { status: 'loaded', data: response.data, headers: { ...response.headers } });
    } catch (e) {
      this.files.set(key, { status: 'error' });
    }
  }

  reset() {
    this.loanId = null;
    this.sections = [];
    this.filter = 'ALL';
    this.expandedSection = null;
    this.focusedField = null;
    this.selectedFileBySection.clear();
    this.files.clear();
    this.dirtySections.clear();
    this.editingNotes.clear();
    this.noteOriginals.clear();
    this.editVersions.clear();
    this.loading = false;
    this.saving = false;
    this.loadError = false;
    this.lastSavedAt = null;
    this.reviewLogStore.reset();
  }
}
